import { Button } from "~/components/ui-custom/button";
import { Form } from "~/components/ui/form";
import { V1PageWrapper } from "~/components/ui-custom/page";
import { Spacer } from "~/components/ui-custom/spacer";
import { H1, P1, P2, P3 } from "~/components/ui-custom/text";
import type { MetaFunction, LoaderFunction } from "react-router";
import { useAction } from "~/hooks/use-action";
import { useLoginForm } from "~/hooks/use-login-form";
import { action_handler } from "~/lib/actions/payloads.server";
import { useSearchParams } from "~/hooks/use-search-params";
import { useI18n } from "~/hooks/use-i18n";
import { browserIdentifyEvent, browserTrackEvent } from "~/lib/analytics/analyticsBrowser.events.client";
import { requireNoUserAndRedirect } from "~/lib/auth/utils.user.server";

export const meta: MetaFunction = () => [
  { title: "Login - Odyssey Portal" },
  { name: "description", content: "Login to your Odyssey Portal account" },
];

/**
 * Loader function
 *
 *
 */
export const loader: LoaderFunction = async ({ request }) => {
  // require no user (redirects logged-in users away from login page)
  await requireNoUserAndRedirect(request);

  return null;
};

/**
 * Action function
 *
 *
 */
export const action = action_handler;

/**
 * Component
 *
 */
export default function Login() {
  //
  // get the search params
  //
  const { searchParamsObj } = useSearchParams();
  //
  // actions
  //
  const loginAction = useAction("auth.login", {
    onError(error) {
      // track the event
      browserTrackEvent("Error Occurred", {
        actionId: "login-form-submission-failed",
        errorMessageSafe: error.message_safe,
        errorMessageUnsafe: error.message_unsafe,
      });
    },
  });
  const { l } = useI18n();

  //
  // form
  //
  const { form, formSchema, onSubmit, formComponents } = useLoginForm({
    values: {
      // decode base64
      email: searchParamsObj?.["login.e"] ? atob(searchParamsObj["login.e"]) : "",
      accessCode: searchParamsObj?.["login.ac"] ? atob(searchParamsObj["login.ac"]) : "",
    },
    onSubmit: (data) => {
      loginAction.submit(data);

      // track the event
      browserTrackEvent("Login Form Submitted", {
        email: data.email,
        accessCode: data.accessCode,
      });
    },
  });

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary-900 to-primary-600 pt-4 pb-20">
      <div className="backdrop-blur-md bg-white/70 shadow-2xl rounded-3xl w-full max-w-md mx-4">
        <div className="flex justify-center">
          <img src="/odyssey-logo.png" alt="Odyssey logo" className="w-52 sm:w-64 my-10" />
        </div>

        <div className="-mt-6 px-8 pb-10 pt-4">
          {/* redirectTo banner */}
          {searchParamsObj?.redirectTo && (
            <>
              <div className="bg-red-100 text-red-900 p-4 rounded-lg text-center">
                <P3>
                  {l(
                    "Welcome back! To continue, please login to your account.",
                    "Pour continuer, veuillez vous connecter à votre compte."
                  )}
                </P3>
              </div>
              <Spacer size="md" />
            </>
          )}

          <h1 className="text-2xl font-semibold text-center text-slate-800">{l("Welcome", "Bienvenue")}</h1>
          <p className="text-sm text-center text-slate-600 mt-1">
            {l(
              "Enter the credentials from your email invitation",
              "Entrez les identifiants de votre invitation par email"
            )}
          </p>

          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-6 mt-8">
              {Object.entries(formComponents).map(([k, comp]) => (
                <div key={k}>{comp}</div>
              ))}

              <Button
                type="submit"
                variant="secondary-green-filled-lg"
                className="w-full font-bold tracking-wide"
                disabled={loginAction.state === "submitting"}
              >
                {loginAction.state === "submitting" ? l("Entering…", "Entrée…") : l("Enter", "Entrer")}
              </Button>

              <P3 center>
                {l("Need help?", "Besoin d'aide?")}{" "}
                <a className="underline" href="mailto:registration@synapsemedcom.ca" target="_blank">
                  {l("Contact support", "Contactez le support")}
                </a>{" "}
                {l("for assistance.", "pour l'assistance.")}
              </P3>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
